import React, { useEffect, useState } from "react";
import AlertMessage from "../../../components/AlertMessage/AlertMessage";
import GearSpinner from "../../../components/GearSpinner/GearSpinner";
import Loader from "../../../components/Loader/Loader";
import PurchaseOrderService from "../../../services/PurchaseOrderService/PurchaseOrderService";
import Select from 'react-select';
import moment from "moment";
import saveIcon from '../../../images/savedraft-icon.png';
import saveIcon2 from '../../../images/save-icon-white.png';
import shareIcon from '../../../images/share-img.png';
import addressBookIcon from '../../../images/address-book-icon-white.png';
import infoIcon from '../../../images/info-circle-fill.png';
import CompanyProfileModal from '../../../components/Modals/CompanyProfileModal';
import AddressModal from '../../../components/Modals/AddressModal';
import SaveAddressModal from '../../../components/Modals/SaveAddressModal';
import SharePurchaseOrderModal from '../../../components/Modals/SharePurchaseOrderModal';
import PurchaseOrderTable from '../tables/PurchaseOrderTable';
import Tooltip from '../../../components/Tooltip/Tooltip';
import '../print.css';
import { formatUSD } from '../../../utils/common';
import { Validation } from "../../../utils/validation";
import { getAuthUser } from "../../../utils/user";

const POGenerator = ({ active, user }) => {
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [isProducts, setIsProducts] = useState(false);
    const [products, setProducts] = useState([]);
    const [productMap, setProductMap] = useState({});
    const [multiplierMap, setMultiplierMap] = useState({});
    const [showSpinner, setShowSpinner] = useState(true);
    const [showLoader, setShowLoader] = useState(false);
    const [estTime, setEstTime] = useState("");
    const [formData, setFormData] = useState({
        order_date: moment().format('YYYY-MM-DD'),
        fob_point: "Origin",
        sub_total: "0.00",
        sales_tax: "0.00",
        total: "0.00",
        sales_data: [{
            type: "Sale",
            product_number: "",
            description: "",
            unit_price: "0.00",
            quantity: "",
            total_price: "0.00"
        }]
    });
    const [sameAsBilling, setSameAsBilling] = useState(false);
    const [companyData, setCompanyData] = useState({});
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [showSaveAddressModal, setShowSaveAddressModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [saveAddressType, setSaveAddressType] = useState('shipping');
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [shippingDraft, setShippingDraft] = useState("");
    const [editingAddressId, setEditingAddressId] = useState(null);
    const [editingAddress, setEditingAddress] = useState(null);
    const [poNumberError, setPoNumberError] = useState("");
    const [shippingAddressError, setShippingAddressError] = useState("");
    const [rowErrors, setRowErrors] = useState({});
    const poCheckCacheRef = React.useRef({ number: '', exists: false, isChecked: false });

    const purchaseOrderService = new PurchaseOrderService();

    useEffect(() => {
        setEstTime(
            moment()
                .tz("America/New_York")
                .format("MMMM DD, YYYY hh:mm:ss A z")
        );
    }, []);

    const fetchedKeyRef = React.useRef("");

    useEffect(() => {
        if (!active) return;

        const currentUser = (user && (user.id || user.name || user.company)) ? user : getAuthUser();
        const userCompanyName = currentUser?.company || "";
        const userId = currentUser?.id || "";

        const fetchKey = `${userId}_${userCompanyName}`;
        if (fetchedKeyRef.current === fetchKey) return;
        fetchedKeyRef.current = fetchKey;

        setShowSpinner(true);
        //setShowLoader(true);
        purchaseOrderService.getCompanyDetailsAndProducts({
            "user_id": userId,
            "company_name": userCompanyName
        }).then(response => {
            setShowSpinner(false);
            setShowLoader(false);
            const data = response?.data;
            if (data && data.status === "success") {
                const companyDetails = data.result.company_details || {};
                setCompanyData(companyDetails);

                const shippingList = Array.isArray(companyDetails.shipping_address)
                    ? companyDetails.shipping_address
                    : [];
                setSavedAddresses(shippingList);

                // 1️⃣ Build multiplier map
                const mMap = {};
                (companyDetails.multipliers || []).forEach(m => {
                    mMap[m.multiplier_group] = Number(m.customer_multiplier);
                });
                setMultiplierMap(mMap);

                // 2️⃣ Build product map (num → product)
                const rawProducts = Array.isArray(data.result.products)
                    ? data.result.products
                    : Object.values(data.result.products || {}).flat();

                const pMap = {};
                const productOptions = [];

                rawProducts.forEach(p => {
                    pMap[p.num] = p;
                    productOptions.push({
                        value: p.num,
                        label: p.num
                    });
                });

                if (rawProducts.length > 0) {
                    setIsProducts(true);
                }

                setProductMap(pMap);
                setProducts(productOptions);

                const billingAddrStr = companyDetails.billing_address
                    ? [
                        companyDetails.billing_address.address,
                        companyDetails.billing_address.city,
                        companyDetails.billing_address.state_name,
                        companyDetails.billing_address.zip
                    ].filter(Boolean).join(', ')
                    : '';

                const companyStreet = companyDetails.address || "";
                const companyCityStateZip = [companyDetails.city, companyDetails.state_name, companyDetails.zip].filter(Boolean).join(', ');
                const companyAddrFull = [companyStreet, companyCityStateZip].filter(Boolean).join(', ');
                const logoImageName = companyDetails.company_logo_name || (companyDetails.company_logo ? companyDetails.company_logo.split('/').pop().split('?')[0] : '') || "";

                setFormData({
                    "company_name": companyDetails.company_name || userCompanyName,
                    "rep": companyDetails.initials || null,
                    "payment_terms": companyDetails.payment_terms || null,
                    "fob_point": "Origin",
                    "carrier": null,
                    "ship_service": null,
                    "date_schedule": null,
                    "billing_address": billingAddrStr,
                    "shipping_address": null,
                    "order_date": moment().format('YYYY-MM-DD'),
                    "sub_total": "0.00",
                    "sales_tax": "0.00",
                    "total": "0.00",
                    "notes": null,
                    "user_id": currentUser?.id,
                    "company_logo": logoImageName,
                    "company_initials": companyDetails.company_initials || "",
                    "company_address": companyAddrFull,
                    "company_street_address": companyStreet,
                    "company_city_state_zip": companyCityStateZip,
                    "sales_data": [{
                        "type": "Sale",
                        "product_number": "",
                        "description": "",
                        "unit_price": "0.00",
                        "quantity": "",
                        "total_price": "0.00"
                    }],
                });
            }
        }).catch(error => {
            console.error('Error fetching company details and products:', error);
            fetchedKeyRef.current = "";
            setShowSpinner(false);
            setShowLoader(false);
        });

    }, [active, user]);

    if (!active) return null;


    const getCustomerData = () => {
        // userServices.getCustomerportal_user_id: user.id, portal_user_name: user.name, customer_from: 'fishbowl' }).then(response => {
        //     const data = response.data;
        //     if (data.status === 'success') {
        //         const mappedCustomers = data.result.map(c => ({
        //             value: c.id,
        //             label: c.name,
        //         }));
        //         setCustomers(mappedCustomers);
        //         if (mappedCustomers.length > 0) {
        //             updateCustomers(mappedCustomers);
        //         }
        //     }
        //     setShowSpinner(false);
        // }).catch(error => {
        //     console.error('Error fetching customer data:', error);
        // });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'order_number') {
            poCheckCacheRef.current = { number: '', exists: false, isChecked: false };
            if (!value || !value.trim()) {
                setPoNumberError("PO Number is mandatory.");
            } else {
                const isValid = /^[a-zA-Z0-9\-_/]*$/.test(value);
                if (!isValid) {
                    setPoNumberError("PO Number can only contain letters, numbers, hyphens (-), underscores (_), and slashes (/).");
                } else {
                    setPoNumberError("");
                }
            }
        }
        if (name === 'shipping_address') {
            if (!value || !value.trim()) {
                setShippingAddressError("Shipping Address is mandatory.");
            } else {
                setShippingAddressError("");
            }
        }
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const checkPoUniqueness = async (poNum) => {
        const trimmed = (poNum || "").trim();
        if (!trimmed) return false;

        if (poCheckCacheRef.current.isChecked && poCheckCacheRef.current.number === trimmed) {
            if (poCheckCacheRef.current.exists) {
                setPoNumberError(`PO Number "${trimmed}" already exists.`);
                return false;
            }
            setPoNumberError("");
            return true;
        }

        try {
            const response = await purchaseOrderService.checkPoNumberExists({ order_number: trimmed });
            const exists = response?.data?.status === "success" && Boolean(response?.data?.exists);
            poCheckCacheRef.current = { number: trimmed, exists: exists, isChecked: true };

            if (exists) {
                setPoNumberError(`PO Number "${trimmed}" already exists.`);
                return false;
            } else {
                setPoNumberError("");
                return true;
            }
        } catch (err) {
            console.error("Error checking PO number uniqueness:", err);
            return true;
        }
    };

    const handlePoNumberBlur = async () => {
        const poNum = (formData.order_number || "").trim();
        if (!poNum) {
            setPoNumberError("PO Number is mandatory.");
            return;
        }
        const isValid = /^[a-zA-Z0-9\-_/]*$/.test(poNum);
        if (!isValid) {
            setPoNumberError("PO Number can only contain letters, numbers, hyphens (-), underscores (_), and slashes (/).");
            return;
        }
        await checkPoUniqueness(poNum);
    };

    const handleShippingAddressBlur = () => {
        const addr = (formData.shipping_address || "").trim();
        if (!addr) {
            setShippingAddressError("Shipping Address is mandatory.");
        } else {
            setShippingAddressError("");
        }
    };

    const validateLocalForm = () => {
        const poNum = (formData.order_number || "").trim();
        if (!poNum) {
            setPoNumberError("PO Number is mandatory.");
            setAlert({ type: 'danger', message: 'PO Number is mandatory. Please enter a PO Number.' });
            return false;
        }
        const isValid = /^[a-zA-Z0-9\-_/]*$/.test(poNum);
        if (!isValid) {
            setPoNumberError("PO Number can only contain letters, numbers, hyphens (-), underscores (_), and slashes (/).");
            setAlert({ type: 'danger', message: 'Please fix PO Number errors before proceeding.' });
            return false;
        }

        const shippingAddr = (formData.shipping_address || "").trim();
        if (!shippingAddr) {
            setShippingAddressError("Shipping Address is mandatory.");
            setAlert({ type: 'danger', message: 'Shipping Address is mandatory. Please enter or select a Shipping Address.' });
            return false;
        }

        if (!validateSalesRows()) {
            setAlert({ type: 'danger', message: 'Please select product number and quantity for all items.' });
            return false;
        }

        return true;
    };

    const validateSalesRows = () => {
        let currentErrors = {};
        (formData.sales_data || []).forEach((row, index) => {
            const errors = Validation(row, ['product_number', 'quantity']);
            if (errors && Object.keys(errors).length > 0) {
                currentErrors[index] = errors;
            }
        });
        setRowErrors(currentErrors);
        return Object.keys(currentErrors).length === 0;
    };

    const resetForm = () => {
        poCheckCacheRef.current = { number: '', exists: false, isChecked: false };
        setFormData(prev => ({
            ...prev,
            order_number: '',
            carrier: null,
            shipping_address: null,
            ship_service: '',
            date_schedule: '',
            notes: '',
            sub_total: '0.00',
            sales_tax: '0.00',
            total: '0.00',
            sales_data: [{
                type: 'Sale',
                product_number: '',
                description: '',
                unit_price: '0.00',
                quantity: '',
                total_price: '0.00'
            }]
        }));
        setSameAsBilling(false);
        setPoNumberError('');
        setShippingAddressError('');
        setRowErrors({});
    };

    const openMainOfficeAddressModal = (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setShowCompanyModal(true);
    };

    const extractAddressByComma = (text) => {
        if (!text || typeof text !== 'string') {
            return { address: '', address_2: '', city: '', state: '', zip: '', country: 'United State' };
        }

        const parts = text.split(',').map(p => p.trim()).filter(Boolean);
        if (parts.length === 0) {
            return { address: '', address_2: '', city: '', state: '', zip: '', country: 'United State' };
        }

        let country = 'United State';
        let zip = '';
        let state = '';
        let city = '';
        let address_2 = '';
        let address = '';

        const isZip = (str) => /^\d{4,5}(-\d{4})?$/.test(str) || (str && str.length <= 10 && /\d/.test(str));
        const lastPart = parts[parts.length - 1];

        if (parts.length >= 5 && !isZip(lastPart)) {
            country = lastPart;
            zip = parts[parts.length - 2];
            state = parts[parts.length - 3];
            city = parts[parts.length - 4];
            const streetParts = parts.slice(0, parts.length - 4);
            address = streetParts[0] || '';
            address_2 = streetParts.slice(1).join(', ') || '';
        } else if (parts.length >= 4 && isZip(lastPart)) {
            country = 'United State';
            zip = lastPart;
            state = parts[parts.length - 2];
            city = parts[parts.length - 3];
            const streetParts = parts.slice(0, parts.length - 3);
            address = streetParts[0] || '';
            address_2 = streetParts.slice(1).join(', ') || '';
        } else if (parts.length === 4) {
            if (isZip(lastPart)) {
                zip = lastPart;
                state = parts[2];
                city = parts[1];
                address = parts[0];
            } else {
                country = lastPart;
                state = parts[2];
                city = parts[1];
                address = parts[0];
            }
        } else if (parts.length === 3) {
            state = parts[2];
            city = parts[1];
            address = parts[0];
        } else if (parts.length === 2) {
            city = parts[1];
            address = parts[0];
        } else {
            address = parts[0];
        }

        return {
            address: address || '',
            address_2: address_2 || '',
            city: city || '',
            state: state || '',
            zip: zip || '',
            country: country || 'United State'
        };
    };

    const openSaveAddressModal = () => {
        setSaveAddressType('shipping');
        const rawAddr = formData.shipping_address || '';
        const parsedAddress = extractAddressByComma(rawAddr);
        setEditingAddress(parsedAddress);
        setShippingDraft(rawAddr);
        setShowSaveAddressModal(true);
    };

    const openAddressBook = () => {
        setShowAddressModal(true);
    };

    const handleSameAsBilling = (e) => {
        const checked = e.target.checked;
        setSameAsBilling(checked);

        if (checked) {
            const billingAddr = formData.billing_address || '';
            setFormData(prev => ({
                ...prev,
                shipping_address: billingAddr
            }));
            if (billingAddr.trim()) {
                setShippingAddressError('');
            } else {
                setShippingAddressError('Shipping Address is mandatory.');
            }
        } else {
            setFormData(prev => ({
                ...prev,
                shipping_address: ''
            }));
            setShippingAddressError('Shipping Address is mandatory.');
        }
    };

    const formatAddressString = (addr) => {
        if (!addr) return '';
        if (typeof addr === 'string') return addr;
        const parts = [
            addr.address,
            addr.address_2,
            addr.city,
            addr.state || addr.state_name,
            addr.zip,
            addr.country
        ].filter(Boolean);
        return parts.join(', ');
    };

    const selectSavedAddress = (address) => {
        const formatted = formatAddressString(address);
        setFormData(prev => ({
            ...prev,
            shipping_address: formatted
        }));
        if (formatted.trim()) {
            setShippingAddressError('');
        }
        setShowAddressModal(false);
    };

    const deleteSavedAddress = async (addressId) => {
        try {
            setShowSpinner(true);
            const response = await purchaseOrderService.deleteAddress({
                user_id: user?.id,
                company_name: formData.company_name || user?.company,
                address_id: addressId
            });
            if (response?.data?.status === 'success') {
                setSavedAddresses(prev => prev.filter(addr => (addr.id || addr.address_id) !== addressId));
                setAlert({ type: 'success', message: 'Address deleted successfully.' });
            } else {
                setAlert({ type: 'danger', message: response?.data?.message || 'Failed to delete address.' });
            }
        } catch (err) {
            console.error('Error deleting address:', err);
            setAlert({ type: 'danger', message: 'Failed to delete address.' });
        } finally {
            setShowSpinner(false);
        }
    };

    const handleEditAddress = (address) => {
        setEditingAddress(address);
        setSaveAddressType('shipping');
        setShippingDraft(formatAddressString(address));
        setShowAddressModal(false);
        setShowSaveAddressModal(true);
    };

    const handleSaveAddress = async (addressPayload) => {
        try {
            setShowSpinner(true);
            const payload = {
                ...addressPayload,
                user_id: user?.id,
                company_id: companyData?.id,
                company_name: formData.company_name || user?.company,
            };
            const response = await purchaseOrderService.saveAddress(payload);
            if (response?.data?.status === 'success') {
                setAlert({ type: 'success', message: response?.data?.message || 'Address saved successfully.' });
                const savedItem = response.data.result || payload;
                const newAddrStr = formatAddressString(savedItem);
                setFormData(prev => ({
                    ...prev,
                    shipping_address: newAddrStr
                }));
                if (newAddrStr.trim()) {
                    setShippingAddressError('');
                }
                if (payload.id || payload.address_id) {
                    setSavedAddresses(prev => prev.map(a => ((a.id || a.address_id) === (payload.id || payload.address_id) ? savedItem : a)));
                } else {
                    setSavedAddresses(prev => [...prev, savedItem]);
                }
                setShowSaveAddressModal(false);
                setEditingAddress(null);
            } else {
                setAlert({ type: 'danger', message: response?.data?.message || 'Failed to save address.' });
            }
        } catch (err) {
            console.error('Error saving address:', err);
            setAlert({ type: 'danger', message: 'Failed to save address.' });
        } finally {
            setShowSpinner(false);
        }
    };

    const handleSubmit = async () => {
        if (!validateLocalForm()) {
            return;
        }
        setShowLoader(true);
        try {

            const est_time = moment()
                .tz("America/New_York")
                .format("MMMM DD, YYYY hh:mm:ss A z");

            const logoImageName = companyData?.company_logo_name
                || (companyData?.company_logo ? companyData.company_logo.split('/').pop().split('?')[0] : '')
                || (formData.company_logo ? formData.company_logo.split('/').pop().split('?')[0] : '')
                || "";

            const payload = {
                ...formData,
                company_id: companyData?.id,
                company_street_address: companyData?.address || formData.company_street_address,
                city: companyData?.city || "",
                state: companyData?.state_name || companyData?.state || "",
                zip: companyData?.zip || "",
                country: companyData?.country || "USA",
                company_logo: logoImageName,
                user_id: user?.id,
                portal_user_name: user?.name,
                est_timestamp: est_time
            };

            //console.log("=== Save Purchase Order Request Payload ===", payload);

            const response = await purchaseOrderService.savePurchaseOrderData(payload);
            const data = response?.data;

            if (data && data.status === 'success') {
                setAlert({ type: 'success', message: data.message || 'Purchase Order saved successfully.' });
                resetForm();
            } else {
                const errMsg = data?.message || 'Failed to save Purchase Order.';
                setAlert({ type: 'danger', message: errMsg });
                if (data?.error === 'po_number_exists' || (errMsg && errMsg.toLowerCase().includes('already exists'))) {
                    setPoNumberError(errMsg);
                }
                if (data?.error === 'shipping_address_required' || (errMsg && errMsg.toLowerCase().includes('shipping address is mandatory'))) {
                    setShippingAddressError(errMsg);
                }
            }
        } catch (error) {
            console.error("Error saving purchase order:", error);
            const errMsg = error?.response?.data?.message || 'Failed to save Purchase Order. Please try again.';
            setAlert({ type: 'danger', message: errMsg });
            if (error?.response?.data?.error === 'po_number_exists' || (errMsg && errMsg.toLowerCase().includes('already exists'))) {
                setPoNumberError(errMsg);
            }
            if (error?.response?.data?.error === 'shipping_address_required' || (errMsg && errMsg.toLowerCase().includes('shipping address is mandatory'))) {
                setShippingAddressError(errMsg);
            }
        } finally {
            setShowLoader(false);
        }
    };

    const handleShareClick = () => {
        if (!validateLocalForm()) {
            return;
        }
        setShowShareModal(true);
    };

    const handleShare = async (email, note, cc) => {
        setShowShareModal(false);
        setShowLoader(true);
        const est_time = moment()
            .tz("America/New_York")
            .format("MMMM DD, YYYY hh:mm:ss A z");

        const ccList = cc ? (Array.isArray(cc) ? cc : cc.split(',').map(c => c.trim()).filter(Boolean)) : [];

        const logoImageName = companyData?.company_logo_name
            || (companyData?.company_logo ? companyData.company_logo.split('/').pop().split('?')[0] : '')
            || (formData.company_logo ? formData.company_logo.split('/').pop().split('?')[0] : '')
            || "";

        const sharePayload = {
            ...formData,
            company_id: companyData?.id,
            company_street_address: companyData?.address || formData.company_street_address,
            city: companyData?.city || "",
            state: companyData?.state_name || companyData?.state || "",
            zip: companyData?.zip || "",
            country: companyData?.country || "USA",
            company_logo: logoImageName,
            user_id: user?.id,
            portal_user_name: user?.name,
            est_timestamp: est_time,
            email_to: [email],
            email_cc: ccList,
            po_note: note,
            format: 'pdf',
            order_date: formData.order_date ? moment(formData.order_date).format('MM/DD/YYYY') : '',
            date_schedule: formData.date_schedule ? moment(formData.date_schedule).format('MM/DD/YYYY') : '',
            sub_total: formatUSD(formData.sub_total || 0),
            sales_tax: formatUSD(formData.sales_tax || 0),
            total: formatUSD(formData.total || 0),
            sales_data: (formData.sales_data || []).map(item => ({
                ...item,
                unit_price: formatUSD(item.unit_price || 0),
                net_price: formatUSD(item.net_price || 0),
                total_price: formatUSD(item.total_price || 0)
            }))
        };

        try {
            const response = await purchaseOrderService.sharePurchaseOrder(sharePayload);

            if (response?.data?.response !== 'success' && response?.status !== 200) {
                const errMsg = response?.data?.message || 'Share failed';
                if (response?.data?.error === 'po_number_exists' || (errMsg && errMsg.toLowerCase().includes('already exists'))) {
                    setPoNumberError(errMsg);
                }
                throw new Error(errMsg);
            }

            setAlert({ type: 'success', message: response?.data?.message || 'Purchase Order shared successfully.' });
            resetForm();
            return true;
        } catch (error) {
            const errMsg = error?.response?.data?.message || error?.message || 'Failed to share Purchase Order.';
            if (error?.response?.data?.error === 'po_number_exists' || (errMsg && errMsg.toLowerCase().includes('already exists'))) {
                setPoNumberError(errMsg);
            }
            setAlert({ type: 'danger', message: errMsg });
            throw error;
        } finally {
            setShowLoader(false);
        }
    };

    const renderNoProducts = () => {
        return (
            <div className="text-center my-5 fade-in">
                <div className="p-5 rounded-3 shadow-sm bg-light border d-inline-block">
                    <h3 className="fw-bold text-dark">No Products Found</h3>
                    <p className="text-muted mb-0">
                        This company does not have any products or pricing configured.
                    </p>
                </div>
            </div>
        );
    };

    const getInitials = (name) => {
        if (!name || typeof name !== 'string') return 'CO';
        const words = name.trim().split(/\s+/);
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const renderCompanyDeatails = () => {
        return (
            <React.Fragment>
                <div className="row">
                    <div className="col-md-12">
                        <div className="section-div1">
                            <div className="row">
                                <div className="col-md-8">
                                    {companyData?.company_logo ? (
                                        <img
                                            src={companyData.company_logo}
                                            alt={companyData?.company_name || ''}
                                            className="po-company-logo"
                                            title="Click to view Company Profile"
                                            style={{ maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', cursor: 'pointer', marginBottom: '10px' }}
                                            onClick={() => setShowCompanyModal(true)}
                                        />
                                    ) : (
                                        <div
                                            className="company-initials-circle"
                                            title="Click to view Company Profile"
                                            onClick={() => setShowCompanyModal(true)}
                                        >
                                            {companyData?.company_initials || getInitials(companyData?.company_name || formData.company_name || user?.company || user?.name)}
                                        </div>
                                    )}
                                    <h3>{companyData?.company_name || formData.company_name}</h3>
                                    <p className="m-0 text-20">{companyData?.company_name || formData.company_name}</p>
                                    {companyData?.address && companyData?.address.trim() ? (
                                        <>
                                            <p className="m-0 text-20">{companyData?.address}</p>
                                            <p className="m-0 text-20">
                                                {[companyData?.city, companyData?.state_name, companyData?.zip].filter(Boolean).join(', ')}
                                            </p>
                                        </>
                                    ) : (
                                        <p className="m-0 text-20">
                                            Please <a href="#" onClick={openMainOfficeAddressModal} className="text-primary text-decoration-underline" style={{ cursor: 'pointer' }}>click here</a> to update address for your account.
                                        </p>
                                    )}
                                </div>
                                <div className="col-md-4">
                                    <h3 className="text-center">PO Generator</h3>
                                    <table className="table table-bordered quote-table text-center">
                                        <thead>
                                            <tr>
                                                <th>PO Number <span className="text-danger">*</span></th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        id="order_number"
                                                        name="order_number"
                                                        value={formData.order_number || ''}
                                                        onChange={handleInputChange}
                                                        onBlur={handlePoNumberBlur}
                                                        maxLength={50}
                                                        placeholder="PO Number *"
                                                    />
                                                    {poNumberError && <div className="text-danger small mt-1" style={{ fontSize: '11px' }}>{poNumberError}</div>}
                                                </td>
                                                <td>{moment().format('MM/DD/YYYY')}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row mt-2">
                    <div className="col-md-6">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Bill To:</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        <textarea readOnly className="form-control" id="billing_address" name="billing_address" rows="2" placeholder="Billing Address" value={formData.billing_address || ''} onChange={handleInputChange}></textarea>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <table className="table table-bordered equal-table">
                            <tbody>
                                <tr>
                                    <td className="align-middle">Customer: {formData.company_name}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="col-md-6">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th style={{ borderRight: "0" }}>Ship To: US <span className="text-danger">*</span></th>
                                    <th style={{ borderLeft: "0", width: "235px" }}>
                                        <div className="d-flex justify-content-end align-items-center gap-2">
                                            <div className="techtop-shipping-actons mb-0" title="Save this address">
                                                <img src={saveIcon2} alt="Save Address" onClick={openSaveAddressModal} width="18" height="18" style={{ cursor: 'pointer' }} />
                                            </div>
                                            <div className="techtop-shipping-actons mb-0" title="Address Book">
                                                <img src={addressBookIcon} alt="Show Address" onClick={openAddressBook} width="18" height="18" style={{ cursor: 'pointer' }} />
                                            </div>
                                            <label className="same-billing-checkbox-container mb-0" htmlFor="same_billing" title="Use Billing Address as Shipping Address">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="same_billing"
                                                    id="same_billing"
                                                    checked={sameAsBilling}
                                                    onChange={handleSameAsBilling}
                                                />
                                                <span>Same</span>
                                            </label>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td colSpan="2">
                                        <textarea
                                            className="form-control"
                                            id="shipping_address"
                                            rows="2"
                                            placeholder="Shipping Address *"
                                            name="shipping_address"
                                            value={formData.shipping_address || ''}
                                            onChange={handleInputChange}
                                            onBlur={handleShippingAddressBlur}
                                        ></textarea>
                                        {shippingAddressError && <div className="text-danger small mt-1" style={{ fontSize: '11px' }}>{shippingAddressError}</div>}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <table className="table table-bordered equal-table">
                            <tbody>
                                <tr>
                                    <td className="align-middle">&nbsp;</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-12">
                        <div className="table-responsive">
                            <table className="table table-bordered techtop-purchase-order-table text-center">
                                <thead>
                                    <tr>
                                        <th>Sales Rep</th>
                                        <th>Payment Terms</th>
                                        <th>FOB Point</th>
                                        <th style={{ width: "15%" }}>Carrier</th>
                                        <th>
                                            Ship Service{' '}
                                            <Tooltip message="Additional Information: Include customer account numbers, preferred freight carrier information, dock hours, delivery appointments, lift gate requirements, or any special shipping instructions that should accompany this purchase order.">
                                                <span
                                                    className="text-info"
                                                    style={{ cursor: 'pointer', fontSize: '14px' }}
                                                >
                                                    &#9432;
                                                </span>
                                            </Tooltip>
                                        </th>
                                        <th>Date Scheduled</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{formData.rep}</td>
                                        <td>{formData.payment_terms}</td>
                                        <td>{formData.fob_point}</td>
                                        <td>
                                            <select className="form-select" aria-label="Default select example" name="carrier" id="carrier" value={formData.carrier} onChange={handleInputChange}>
                                                <option selected>Select Carrier</option>
                                                <option value="UPS">UPS</option>
                                                <option value="Will Call">Will Call</option>
                                                <option value="LTL">LTL</option>
                                                <option value="Courier">Courier</option>
                                                <option value="3rd Part Billing">3rd Part Billing</option>
                                            </select>
                                        </td>
                                        <td>
                                            <input type="text" className="form-control" placeholder="Ship Service" name="ship_service" id="ship_service" value={formData.ship_service} onChange={handleInputChange} />
                                        </td>
                                        <td><input type="date" className="form-control" name="date_schedule" id="date_schedule" value={formData.date_schedule} onChange={handleInputChange} /></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        )
    }

    const renderSalesTable = () => {
        return (
            <React.Fragment>
                <div className="row">
                    <div className="col-md-12">
                        <div className="table-responsive">
                            {/* {console.log("Rendering PurchaseOrderTable with salesData:", formData.sales_data)} */}
                            <PurchaseOrderTable
                                salesData={formData.sales_data}
                                setFormData={setFormData}
                                products={products}
                                productMap={productMap}
                                multiplierMap={multiplierMap}
                                rowErrors={rowErrors}
                            />


                        </div>
                        <div className="table-responsive mt-5">
                            <table className="table table-bordered techtop-purchase-order-table2">
                                <tbody>
                                    <tr>
                                        <td rowSpan="3">
                                            <label>Notes:</label>
                                            <textarea className="form-control" id="notes" rows="2" placeholder="Notes (max 1000 characters)" name="notes" value={formData.notes || ''} onChange={handleInputChange} maxLength={1000}></textarea>
                                            <div className="text-end text-muted small mt-1" style={{ fontSize: '11px' }}>{(formData.notes || '').length} / 1000 characters</div>
                                        </td>
                                        <td className="text-end"><strong>Subtotal:</strong></td>
                                        <td className="text-end"><strong>${formatUSD(formData.sub_total)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="text-end"><strong>Sales Tax:</strong></td>
                                        <td className="text-end"><strong>${formatUSD(formData.sales_tax)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td className="text-end"><strong>Total:</strong></td>
                                        <td className="text-end"><strong>${formatUSD(formData.total)}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p className="dark-color1">{estTime}</p>
                    </div>
                </div>
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <main className="techtop-middle-div">
                <section className="techtop-quality-control-section pt-2 pb-3">
                    <div className="container tab-pane active">
                        <div className="row mb-4">
                            <div className="col-md-12 text-center">
                                <p style={{ fontStyle: "italic", fontSize: "14px", color: "#333", maxWidth: "95%", margin: "0 auto", lineHeight: "1.6" }}>
                                    Before using the PO Generator, please take a moment to personalize your account by uploading your company logo and verifying your company name and address in your account settings. This information will automatically appear on all generated purchase orders, giving them a professional, branded look. Taking a few minutes to complete your profile will help ensure your purchase orders are accurate and ready to submit. If your company information changes, simply update your account to keep future POs current.
                                </p>
                            </div>
                        </div>
                        <div className="row">
                            <AlertMessage
                                type={alert.type}
                                message={alert.message}
                                onClose={() => setAlert({ type: '', message: '' })}
                            />
                            <div className="col-md-10" id="printable">
                                {isProducts && (
                                    <React.Fragment>
                                        {renderCompanyDeatails()}
                                        {renderSalesTable()}
                                    </React.Fragment>
                                )}
                            </div>

                            {!showSpinner && !isProducts && renderNoProducts()}

                            {isProducts && (
                                <React.Fragment>
                                    <div className="col-md-2">
                                        <ul className="net-pricing-report-list">
                                            <li><button className="btn-link-style" onClick={() => handleSubmit()}><img src={saveIcon} alt="Save" /> Save</button></li>
                                            <li><button className="btn-link-style" onClick={() => handleShareClick()}><img src={shareIcon} alt="Share" /> Share</button></li>
                                        </ul>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                    {showSpinner && <GearSpinner />}
                    <Loader isLoading={showLoader} message="Processing..." />

                    <AddressModal
                        show={showAddressModal}
                        savedAddresses={savedAddresses}
                        onClose={() => setShowAddressModal(false)}
                        onSelectAddress={selectSavedAddress}
                        onDeleteAddress={deleteSavedAddress}
                        onEditAddress={handleEditAddress}
                    />

                    <SaveAddressModal
                        show={showSaveAddressModal}
                        shippingDraft={shippingDraft}
                        editingAddress={editingAddress}
                        addressType={saveAddressType}
                        onClose={() => { setShowSaveAddressModal(false); setEditingAddress(null); }}
                        company_name={formData.company_name || companyData.company_name}
                        company_id={formData.company_id || companyData.id}
                        onSaveAddress={handleSaveAddress}
                    />

                    <CompanyProfileModal
                        show={showCompanyModal}
                        companyData={companyData}
                        onClose={() => setShowCompanyModal(false)}
                    />

                    <SharePurchaseOrderModal
                        isShow={showShareModal}
                        onClose={() => setShowShareModal(false)}
                        onShare={handleShare}
                    />
                </section>
            </main>
        </React.Fragment >
    )
}

export default POGenerator;